import { Button, Typography } from "@/components/atoms";
import {
  BankTransferTileIcon,
  ChevronRightIcon,
  DirhamIcon,
  DirhamSymbolIcon,
  ProofOfMortgageTileIcon,
} from "@/components/atoms/icons";
import { FullScreenLoader, OkDialog } from "@/components/molecules";
import { Toast } from "@/components/molecules/Toast";
import { TextHeader } from "@/components/molecules/header/TextHeader";
import { UI_PRIMITIVES } from "@/core/constants/uiColors";
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from "@/core/constants/dynatraceCatalog";
import { useCatalogDynatraceView } from "@/core/hooks/useCatalogDynatraceView";
import { useTranslation, useUIColors } from "@/core/hooks";
import { trackCatalogEvent } from "@/core/services/trackDynatraceCatalog";
import { logger } from "@/core/logger";
import { useI18nContext } from "@/core/providers/I18nProvider";
import { TOP_UP_TYPE, updateTopUpType } from "@/modules/properties-web/services/topUpService";
import { router, useLocalSearchParams } from "expo-router";
import type { PaymentFlowReturnTo } from "./SubmitPaymentProofScreen";
import React, { useRef, useState } from "react";
import { Pressable, ScrollView, StyleSheet, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

type PaymentMethod = "bankTransfer" | "proofOfMortgage";

function toNumber(value: string | undefined): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function PaymentMethodRow({
  icon,
  title,
  subtitle,
  selected,
  onPress,
}: Readonly<{
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  selected: boolean;
  onPress: () => void;
}>) {
  const colors = useUIColors();
  const { isRTL } = useI18nContext();

  return (
    <Pressable
      onPress={onPress}
      style={[
        styles.methodRow,
        {
          backgroundColor: colors.backgroundLevel1,
          borderColor: selected ? colors.fontPrimary : colors.backgroundLevel4,
          borderWidth: selected ? 2 : 1,
        },
      ]}
      accessibilityRole="button"
      accessibilityState={{ selected }}
    >
      {icon}
      <View style={styles.methodTextContent}>
        <Typography variant="headline" color="primary">
          {title}
        </Typography>
        <Typography variant="caption" color="tertiary" style={styles.methodSubtitle}>
          {subtitle}
        </Typography>
      </View>
      <View style={{ transform: [{ scaleX: isRTL ? -1 : 1 }] }}>
        <ChevronRightIcon color={colors.fontTertiary} size={20} />
      </View>
    </Pressable>
  );
}

export function CompleteYourPaymentScreen() {
  useCatalogDynatraceView(
    ALMETLAA_SCREEN.TOP_UP,
    ALMETLAA_ACTION.TOP_UP.COMPLETE_PAYMENT_PAGE_VIEW,
    true,
  );
  const colors = useUIColors();
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const { propertyId, unitLabel, totalPrice, iskanLoan, returnTo } = useLocalSearchParams<{
    propertyId?: string;
    unitLabel?: string;
    totalPrice?: string;
    iskanLoan?: string;
    /** Where Pay Now was opened from — the last screen returns there. */
    returnTo?: PaymentFlowReturnTo;
  }>();

  const totalPriceValue = toNumber(totalPrice);
  const iskanLoanValue = toNumber(iskanLoan);
  const outstandingBalance = Math.max(0, totalPriceValue - iskanLoanValue);

  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorDialogVisible, setErrorDialogVisible] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState("");
  const [successToastVisible, setSuccessToastVisible] = useState(false);
  const [successToastMessage, setSuccessToastMessage] = useState("");
  // Toast fires onHide once on mount too (starts hidden) — this guards
  // against treating that spurious call as a real "toast was shown" hide.
  const pendingSuccessNavRef = useRef(false);

  const handleContinue = async () => {
    if (!selectedMethod || isSubmitting) {
      return;
    }
    logger.info("CompleteYourPaymentScreen: Continue pressed", { selectedMethod });

    if (selectedMethod === "bankTransfer") {
      router.push({
        pathname: "/(auth)/bank-transfer-details",
        params: { propertyId, unitLabel, totalPrice, iskanLoan, method: selectedMethod, returnTo },
      } as any);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await updateTopUpType(TOP_UP_TYPE.MORTGAGE);
      logger.info("CompleteYourPaymentScreen: updateTopUpType mortgage response", { response });
      trackCatalogEvent({
        screen: ALMETLAA_SCREEN.TOP_UP,
        actionName: ALMETLAA_ACTION.TOP_UP.SUBMIT_SUCCESS,
        eventType: 'action',
      });
      const message = response.message?.trim() ?? "";
      if (message) {
        setSuccessToastMessage(message);
        pendingSuccessNavRef.current = true;
        setSuccessToastVisible(true);
      } else {
        router.push({
          pathname: "/(auth)/payment-proof-submit",
          params: { propertyId, unitLabel, method: "proofOfMortgage", returnTo },
        } as any);
      }
    } catch (error) {
      logger.error("CompleteYourPaymentScreen: updateTopUpType mortgage failed", error);
      trackCatalogEvent({
        screen: ALMETLAA_SCREEN.TOP_UP,
        actionName: ALMETLAA_ACTION.TOP_UP.SUBMIT_ERROR,
        eventType: 'error',
      });
      const apiMessage =
        error instanceof Error && error.message.trim().length > 0 ? error.message : "";
      setErrorDialogMessage(apiMessage || t("reserveUnit.submissionFailedError"));
      setErrorDialogVisible(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}>
      <TextHeader title={t("propertyDetails.completePaymentTitle")} />
      <ScrollView
        contentContainerStyle={[styles.scrollContent, { paddingBottom: 24 + insets.bottom }]}
        showsVerticalScrollIndicator={false}
      >
        <View
          style={[
            styles.balanceCard,
            { borderColor: colors.backgroundLevel4 },
          ]}
        >
          <Typography variant="caption" color="secondary" style={styles.balanceLabel}>
            {t("propertyDetails.outstandingBalance").toUpperCase()}
          </Typography>
          <View style={styles.balanceAmountRow}>
            <DirhamSymbolIcon size={20} color={UI_PRIMITIVES.redLight} />
            <Typography variant="title2" color={UI_PRIMITIVES.redLight} style={styles.balanceAmountText}>
              {outstandingBalance.toLocaleString()}
            </Typography>
          </View>
          <View style={styles.balanceBreakdown}>
            <Typography variant="caption" color="tertiary">
              {t("reserveUnit.totalUnitPrice")}
            </Typography>
            <DirhamIcon size={12} color={colors.fontTertiary} />
            <Typography variant="caption" color="tertiary">
              {totalPriceValue.toLocaleString()}
            </Typography>
            <Typography variant="caption" color="tertiary">
              {"  •  "}
            </Typography>
            <Typography variant="caption" color="tertiary">
              {t("propertyDetails.approvedIskanLoan")}
            </Typography>
            <DirhamIcon size={12} color={colors.fontTertiary} />
            <Typography variant="caption" color="tertiary">
              {iskanLoanValue.toLocaleString()}
            </Typography>
          </View>
        </View>

        <Typography variant="subhead" color="primary" style={styles.proceedLabel}>
          {t("propertyDetails.howWouldYouLikeToProceed")}
        </Typography>

        <View style={styles.methodsList}>
          <PaymentMethodRow
            icon={<BankTransferTileIcon />}
            title={t("propertyDetails.bankTransfer")}
            subtitle={t("propertyDetails.bankTransferDescription")}
            selected={selectedMethod === "bankTransfer"}
            onPress={() => {
              trackCatalogEvent({
                screen: ALMETLAA_SCREEN.TOP_UP,
                actionName: ALMETLAA_ACTION.TOP_UP.BANK_TRANSFER_SELECT_CLICK,
                eventType: 'action',
              });
              setSelectedMethod("bankTransfer");
            }}
          />
          <PaymentMethodRow
            icon={<ProofOfMortgageTileIcon />}
            title={t("propertyDetails.proofOfMortgage")}
            subtitle={t("propertyDetails.proofOfMortgageDescription")}
            selected={selectedMethod === "proofOfMortgage"}
            onPress={() => {
              trackCatalogEvent({
                screen: ALMETLAA_SCREEN.TOP_UP,
                actionName: ALMETLAA_ACTION.TOP_UP.PROOF_OF_MORTGAGE_SELECT_CLICK,
                eventType: 'action',
              });
              setSelectedMethod("proofOfMortgage");
            }}
          />
        </View>
      </ScrollView>

      <View style={[styles.footer, { paddingBottom: 16 + insets.bottom, backgroundColor: colors.backgroundLevel2 }]}>
        <Button
          title={t("common.cancel")}
          variant="outline"
          onPress={() => router.back()}
          style={styles.footerButton}
          disabled={isSubmitting}
        />
        <Button
          title={t("common.continue")}
          variant="primary"
          disabled={!selectedMethod || isSubmitting}
          onPress={() => {
            void handleContinue();
          }}
          style={styles.footerButton}
        />
      </View>

      <OkDialog
        visible={errorDialogVisible}
        message={errorDialogMessage}
        onOk={() => setErrorDialogVisible(false)}
      />
      <Toast
        visible={successToastVisible}
        message={successToastMessage}
        onHide={() => {
          setSuccessToastVisible(false);
          if (pendingSuccessNavRef.current) {
            pendingSuccessNavRef.current = false;
            logger.info("CompleteYourPaymentScreen: navigating to payment-proof-submit");
            router.push({
              pathname: "/(auth)/payment-proof-submit",
              params: { propertyId, unitLabel, method: "proofOfMortgage", returnTo },
            } as any);
          }
        }}
      />
      <FullScreenLoader visible={isSubmitting} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    gap: 16,
  },
  balanceCard: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    gap: 4,
  },
  balanceLabel: {
    letterSpacing: 0.5,
  },
  balanceAmountRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  balanceAmountText: {
    fontWeight: "700",
  },
  balanceBreakdown: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 2,
    marginTop: 4,
  },
  // Font/Family, Font/Size/XS Medium, weight 500, LH 100%, letter-spacing 0
  // (Typography's "subhead" variant already gives Matter-Medium/14px/LH14).
  proceedLabel: {
    marginTop: 4,
    fontWeight: "500",
    letterSpacing: 0,
  },
  methodsList: {
    gap: 12,
  },
  methodRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    borderRadius: 12,
    padding: 16,
  },
  methodTextContent: {
    flex: 1,
    gap: 2,
  },
  // Font/Family, Font/Size/S Regular, weight 400, LH 100%, letter-spacing 0
  methodSubtitle: {
    fontFamily: "Matter-Regular",
    fontWeight: "400",
    fontSize: 14,
    lineHeight: 14,
    letterSpacing: 0,
  },
  footer: {
    flexDirection: "row",
    gap: 12,
    paddingHorizontal: 16,
    paddingTop: 12,
  },
  footerButton: {
    flex: 1,
  },
});

export default CompleteYourPaymentScreen;
